<script setup lang="ts">
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import type { CheckboxChangeEventDetail } from '@ionic/vue';
import type { ConsentPurpose } from '@/api/user/types/privacy-consents';

const props = defineProps<{ purpose: ConsentPurpose; checked: boolean; disabled?: boolean }>();
const emit = defineEmits<{ change: [granted: boolean] }>();

const { t } = useI18n();
const copyKey = computed(() => `privacy_consents.${props.purpose === 'metaAdMeasurement' ? 'meta' : 'replay'}`);
const detailsId = computed(() => `consent-details-${props.purpose}`);
const expanded = ref(false);

const onChange = (e: CustomEvent<CheckboxChangeEventDetail>) => emit('change', e.detail.checked);
</script>

<template>
  <div class="consent-row" :data-testid="`consent-row-${purpose}`">
    <div class="consent-row__head">
      <div class="consent-row__text">
        <div class="consent-row__title">{{ t(`${copyKey}.title`) }}</div>
        <p class="consent-row__summary">{{ t(`${copyKey}.summary`) }}</p>
      </div>
      <ion-toggle
        :checked="checked"
        :disabled="disabled"
        :aria-label="t(`${copyKey}.title`)"
        data-testid="consent-toggle"
        @ionChange="onChange"
      />
    </div>
    <button
      type="button"
      class="consent-row__more"
      :aria-expanded="expanded"
      :aria-controls="detailsId"
      data-testid="consent-details-toggle"
      @click="expanded = !expanded"
    >
      {{ t(expanded ? 'privacy_consents.details_hide' : 'privacy_consents.details_show') }}
    </button>
    <ul v-if="expanded" :id="detailsId" class="consent-row__details" data-testid="consent-details">
      <li>{{ t(`${copyKey}.what`) }}</li>
      <li>{{ t(`${copyKey}.why`) }}</li>
      <li>{{ t(`${copyKey}.withdraw`) }}</li>
    </ul>
  </div>
</template>

<style scoped lang="scss">
.consent-row {
  padding: 14px 0;

  &__head {
    display: flex;
    align-items: flex-start;
    gap: 12px;
  }

  &__text {
    flex: 1;
    min-width: 0;
  }

  &__title {
    font-size: 16px;
    font-weight: 700;
    color: #f7f7f7;
  }

  &__summary {
    margin: 4px 0 0;
    font-size: 14px;
    color: #a7a7a7;
  }

  &__more {
    margin-top: 6px;
    padding: 4px 0;
    border: 0;
    background: transparent;
    color: var(--ion-color-primary);
    font-size: 14px;
    text-decoration: underline;
  }

  &__details {
    margin: 6px 0 0;
    padding-inline-start: 18px;
    font-size: 13px;
    line-height: 1.5;
    color: #c7c7c7;

    li + li {
      margin-top: 4px;
    }
  }

  ion-toggle {
    flex-shrink: 0;
    --handle-background-checked: var(--ion-color-primary);
    --handle-background: var(--ion-color-medium-light);
    --track-background: #333333;
    --track-background-checked: #333333;
    --handle-width: 24px;
    --handle-height: 24px;
    &::part(track) {
      height: 28px;
    }
  }
}
</style>
